import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { brand, brandTextColor } from "../../../config/theme";
import api from "../../../utils/api";
import { useReportingTrend } from "../../../features/dashboards/queries";
import {
  useReportingSummary,
  useReportingCompanies,
  useReportingUnits,
  computeRiskDistribution,
  type ReportingCompany,
} from "../../../features/dashboards/reporting";
import {
  REPORTING_DEFAULTS,
  classifyRisk,
  RISK_LABELS,
  getEffectivenessBarColor,
  platformLabelOf,
  type ReportingPlatform,
} from "../../../features/dashboards/reportingConfig";
import { toCsv, downloadCsv } from "../../../features/sg/csv";
import Modal from "../../../components/base/Modal";
import ActiveUnitsHistorySection from "../../../components/dashboards/ActiveUnitsHistorySection";
import AltasBajasPlatformCards from "../../../components/dashboards/AltasBajasPlatformCards";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js/auto";
import { fileDate } from "../../../features/sg/exportRows";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sublabel?: string;
  iconBgColor?: string;
}

function StatCard({
  icon,
  label,
  value,
  sublabel,
  iconBgColor = "bg-blue-500",
}: StatCardProps) {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const [displayValue, setDisplayValue] = useState<number | string>(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Animación de conteo cuando el valor cambia
  useEffect(() => {
    if (typeof value === "number" && value !== displayValue) {
      setIsAnimating(true);
      const duration = 1000; // 1.5 segundos
      const steps = 30;
      const currentValue = typeof displayValue === "number" ? displayValue : 0;
      const increment = (value - currentValue) / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.round(currentValue + increment * currentStep);
        setDisplayValue(newValue);

        if (currentStep >= steps) {
          clearInterval(timer);
          setDisplayValue(value);
          setIsAnimating(false);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    } else if (typeof value === "string") {
      setDisplayValue(value);
    }
  }, [value, displayValue]);

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4 flex flex-col justify-between min-h-[100px] sm:min-h-[120px]`}
    >
      <div className="flex items-center justify-between">
        <div
          className={`w-8 h-8 ${iconBgColor} rounded-lg flex items-center justify-center`}
        >
          {icon}
        </div>
        <span className={`text-xs font-medium ${themeClasses.textSecondary}`}>
          {label}
        </span>
      </div>
      <div className="mt-auto">
        <p
          className={`text-lg sm:text-2xl font-bold ${themeClasses.text} ${
            isAnimating ? "transition-all duration-300 ease-out" : ""
          }`}
        >
          {displayValue}
        </p>
        {sublabel && (
          <p className={`text-xs ${themeClasses.textSecondary}`}>{sublabel}</p>
        )}
      </div>
    </div>
  );
}

interface PlatformCardProps {
  icon: React.ReactNode;
  iconBgColor: string;
  name: string;
  label: string;
  percentage: number;
  current: number;
  total: number;
  isDarkMode: boolean;
  themeClasses: ReturnType<typeof getThemeClasses>;
  statusColor?: string;
  t: TFunction<"translation", undefined>;
}

interface BarChartRowProps {
  label: string;
  percentage: number;
  color?: string;
  isDarkMode: boolean;
  themeClasses: ReturnType<typeof getThemeClasses>;
}

interface StackedBarData {
  platform: string;
  totalUnits: number;
  healthy: { averagePercentage: number; companyCount: number; units: number };
  warning: { averagePercentage: number; companyCount: number; units: number };
  highRisk: { averagePercentage: number; companyCount: number; units: number };
  critical: { averagePercentage: number; companyCount: number; units: number };
}

interface StackedBarChartProps {
  data: StackedBarData[];
  isDarkMode: boolean;
  themeClasses: ReturnType<typeof getThemeClasses>;
  t: TFunction<"translation", undefined>;
}

function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700/40 ${className}`}
    />
  );
}

interface PlatformAnalyticsCardProps {
  icon: React.ReactNode;
  iconBgColor: string;
  name: string;
  description: string;
  clientCount: number | string;
  serviceRate: number;
  bestClient: number;
  worstClient: number;
  chartData: (number | null)[];
  chartColor: string;
  isDarkMode: boolean;
  themeClasses: ReturnType<typeof getThemeClasses>;
  t: TFunction<"translation", undefined>;
}

function PlatformAnalyticsCard({
  icon,
  iconBgColor,
  name,
  clientCount,
  serviceRate,
  bestClient,
  worstClient,
  chartData,
  chartColor,
  themeClasses,
  t,
}: PlatformAnalyticsCardProps) {
  const days = [
    t("systemHealth.days.mon"),
    t("systemHealth.days.tue"),
    t("systemHealth.days.wed"),
    t("systemHealth.days.thu"),
    t("systemHealth.days.fri"),
    t("systemHealth.days.sat"),
    t("systemHealth.days.sun"),
  ];
  const minVal = 0;
  const maxVal = 100;
  const range = 100;

  // Construir segmentos separados para los puntos no-null
  const nonNullPoints = chartData
    .map((val, i) =>
      val !== null
        ? {
            x: (i / (chartData.length - 1)) * 100,
            y: 100 - ((val - minVal) / range) * 100,
          }
        : null,
    )
    .filter((p): p is { x: number; y: number } => p !== null);

  const points = nonNullPoints.map((p) => `${p.x},${p.y}`).join(" ");

  // Área de relleno solo hasta el último punto no-null
  const lastPoint = nonNullPoints[nonNullPoints.length - 1];
  const areaPoints =
    nonNullPoints.length > 0
      ? `0,100 ${points} ${lastPoint.x},100`
      : "0,100 100,100";

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-5`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 ${iconBgColor} rounded-lg flex items-center justify-center`}
          >
            {icon}
          </div>
          <div>
            <h3 className={`font-semibold ${themeClasses.text}`}>{name}</h3>
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              {clientCount} {t("systemHealth.clients")}
            </p>
          </div>
        </div>
        {/* <button
          className={`text-sm ${themeClasses.textSecondary} hover:underline`}
        >
          View Details
        </button> */}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("systemHealth.serviceRate")}
          </p>
          <p className={`text-lg sm:text-2xl font-bold ${themeClasses.text}`}>
            {serviceRate}%
          </p>
        </div>
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("systemHealth.bestClient")}
          </p>
          <p className="text-lg sm:text-2xl font-bold text-[#22c55e]">
            {bestClient}%
          </p>
        </div>
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("systemHealth.worstClient")}
          </p>
          <p className="text-lg sm:text-2xl font-bold text-[#ef4444]">
            {worstClient}%
          </p>
        </div>
      </div>

      {/* Chart (mini-tendencia semanal) — oculto: histórico sin datos */}
      {false && (
      <div className="relative h-24">
        {/* Y Axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-right pr-2">
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {Math.round(maxVal)}
          </span>
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {Math.round((maxVal + minVal) / 2)}
          </span>
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {Math.round(minVal)}
          </span>
        </div>

        {/* Chart area */}
        <div className="ml-8 h-full relative">
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {/* Area fill hasta el último punto no-null */}
            <polygon points={areaPoints} fill={chartColor} fillOpacity="0.1" />
            {/* Line solo por puntos no-null */}
            {points && (
              <polyline
                points={points}
                fill="none"
                stroke={chartColor}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>

          {/* X Axis labels */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-between transform translate-y-5">
            {days.map((day) => (
              <span
                key={day}
                className={`text-xs ${themeClasses.textSecondary}`}
              >
                {day}
              </span>
            ))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

interface UnitItem {
  id: string;
  name: string;
  plate: string;
  brand: string;
  model: string;
  companyId?: string;
  companyName?: string;
}

interface CriticalCompany {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  reportingUnits: number;
  totalUnits: number;
  percentage: number;
}

interface PlatformBreakdownData {
  platformName: string;
  platformIcon: React.ReactNode;
  iconBgColor: string;
  clientCount: number;
  totalUnits: number;
  averageService: number;
  healthy: number;
  warning: number;
  highRisk: number;
  critical: number;
  companies?: ReportingCompany[];
}

interface PlatformClientDetailsProps {
  platforms: PlatformBreakdownData[];
  themeClasses: ReturnType<typeof getThemeClasses>;
  t: TFunction<"translation", undefined>;
}

interface HistoricalTrendsProps {
  themeClasses: ReturnType<typeof getThemeClasses>;
}

const MONTH_KEYS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const;

function HistoricalPerformanceTrends({ themeClasses }: HistoricalTrendsProps) {
  const { t } = useTranslation("entregaServicio");
  const [selectedRange, setSelectedRange] = useState<"7" | "30" | "90">("30");
  const trendQuery = useReportingTrend(selectedRange);
  const v5TrendData = trendQuery.data ?? [];
  const loading = trendQuery.isLoading;

  const days = Number(selectedRange);

  const wialonBase = [
    88, 89, 90, 91, 92, 91, 90, 89, 91, 92, 91, 90, 89, 90, 91, 92, 91, 90, 89,
    88, 89, 90, 91, 92, 91, 88, 89, 88.5, 90, 92,
  ];
  const maponBase = [
    93, 94, 93, 94, 95, 94, 93, 94, 95, 94, 93, 94, 95, 94, 93, 94, 95, 94, 93,
    94, 95, 94, 93, 94, 95, 94, 93, 94, 95, 94,
  ];
  const cycleData = (base: number[], length: number) =>
    Array.from({ length }, (_, i) => base[i % base.length]);

  const platforms = [
    // {
    //   name: "V2",
    //   color: "#6366f1",
    //   data: cycleData([97, 97.5, 98, 97, 96.5, 97, 97.8, 98, 97.5, 97, 96, 97, 97.5, 98, 97, 96.5, 97, 97.8, 98, 97.5, 97, 96, 97, 97.5, 95, 96, 97, 96.5, 97, 92], days),
    // },
    // {
    //   name: "V4",
    //   color: "#22c55e",
    //   data: cycleData([96, 95, 96.5, 97, 95.5, 96, 96.8, 97, 96.5, 96, 95.5, 96, 96.5, 97, 97.5, 96.5, 96, 96.8, 97, 96.5, 96, 95.5, 96, 96.5, 97, 97.5, 96, 96.5, 97, 93], days),
    // },
    {
      name: "V5",
      color: "#14b8a6",
      data: v5TrendData.map((d) => d.percentage),
    },
    {
      name: "Wialon",
      color: "#f59e0b",
      data: cycleData(wialonBase, days),
    },
    {
      name: "Mapon",
      color: "#a855f7",
      data: cycleData(maponBase, days),
    },
  ];

  const formatDate = (date: Date) =>
    `${t(`systemHealth.months.${MONTH_KEYS[date.getMonth()]}`)} ${date.getDate()}`;

  const dates =
    v5TrendData.length > 0
      ? v5TrendData.map((d) => formatDate(new Date(d.date + "T12:00:00")))
      : Array.from({ length: days }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (days - 1 - i));
          return formatDate(date);
        });

  const allNonZeroValues = platforms
    .flatMap((p) => p.data)
    .filter((v) => v > 0);
  const hasV5Data = v5TrendData.length > 0;
  const dataMin =
    allNonZeroValues.length > 0 ? Math.min(...allNonZeroValues) : 80;
  const minVal = hasV5Data ? 0 : Math.max(0, Math.floor(dataMin / 5) * 5 - 5);
  const maxVal = 100;
  const chartRange = maxVal - minVal;
  const yAxisSteps = [
    maxVal,
    maxVal - chartRange * 0.25,
    maxVal - chartRange * 0.5,
    maxVal - chartRange * 0.75,
    minVal,
  ];

  const xLabelStep = Math.max(1, Math.floor(days / 6));

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h3
            className={`text-sm sm:text-base font-semibold ${themeClasses.text}`}
          >
            {t("systemHealth.serviceAvailabilityOverTime")}
          </h3>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("systemHealth.serviceAvailabilityOverTimeSubtitle")}
          </p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 self-start sm:self-auto">
          {(["7", "30", "90"] as const).map((range) => (
            <button
              key={range}
              onClick={() => setSelectedRange(range)}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium rounded-md transition-colors ${
                selectedRange === range
                  ? "bg-white dark:bg-gray-700 shadow-sm " + themeClasses.text
                  : themeClasses.textSecondary +
                    " hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {range}d
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-48 sm:h-64" />
          <div className="flex justify-center gap-6 mt-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ) : (
        <>
          <div className="relative h-48 sm:h-64">
            {/* Y Axis */}
            <div className="absolute left-0 top-0 h-full flex flex-col justify-between pr-3">
              {yAxisSteps.map((val) => (
                <span
                  key={val}
                  className={`text-xs ${themeClasses.textSecondary}`}
                >
                  {Math.round(val)}
                </span>
              ))}
            </div>

            {/* Chart Area */}
            <div className="ml-8 h-full relative">
              <svg
                className="w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {/* Grid lines */}
                {[0, 25, 50, 75, 100].map((y) => (
                  <line
                    key={y}
                    x1="0"
                    y1={y}
                    x2="190"
                    y2={y}
                    stroke="currentColor"
                    strokeOpacity="0.1"
                    strokeWidth="0.5"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}

                {/* Platform lines */}
                {platforms.map((platform) => {
                  if (platform.data.length < 2) return null;
                  const points = platform.data
                    .map((val, i) => {
                      const x = (i / (platform.data.length - 1)) * 100;
                      const y = 100 - ((val - minVal) / chartRange) * 100;
                      return `${x},${y}`;
                    })
                    .join(" ");

                  return (
                    <polyline
                      key={platform.name}
                      points={points}
                      fill="none"
                      stroke={platform.color}
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}
              </svg>

              {/* X Axis labels */}
              <div className="absolute bottom-0 left-0 right-0 flex justify-between transform translate-y-6 overflow-hidden">
                {dates
                  .filter(
                    (_, i) => i % xLabelStep === 0 || i === dates.length - 1,
                  )
                  .map((date, i) => (
                    <span
                      key={i}
                      className={`text-xs ${themeClasses.textSecondary} whitespace-nowrap`}
                    >
                      {date}
                    </span>
                  ))}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
            {platforms.map((platform) => (
              <div key={platform.name} className="flex items-center gap-2">
                <div
                  className="w-3 h-0.5 rounded"
                  style={{ backgroundColor: platform.color }}
                ></div>
                <span className={`text-xs ${themeClasses.textSecondary}`}>
                  {platform.name}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PlatformClientDetails({
  platforms,
  themeClasses,
  t,
}: PlatformClientDetailsProps) {
  const exportCsv = (platform: PlatformBreakdownData) => {
    const companies = platform.companies ?? [];
    const headers = [
      "Usuario",
      "Razón social",
      "Total",
      "Reportando",
      "No reportando",
      "Efectividad %",
      "Categoría",
    ];
    const rows = companies.map((c) => [
      c.user_name ?? "",
      c.business_name ?? "",
      c.total,
      c.reporting,
      c.not_reporting,
      c.effectiveness_pct,
      RISK_LABELS[classifyRisk(c.effectiveness_pct)],
    ]);
    downloadCsv(
      `entrega-servicio_${platform.platformName.toLowerCase()}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      toCsv(headers, rows),
    );
  };

  return (
    <div className="space-y-4">
      {platforms.map((platform, index) => (
        <div
          key={index}
          className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}
        >
          {/* Header */}
          <div className="bg-blue-50 dark:bg-blue-900/20 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 ${platform.iconBgColor} rounded-lg flex items-center justify-center flex-shrink-0`}
              >
                {platform.platformIcon}
              </div>
              <div className="min-w-0">
                <h3
                  className={`text-sm sm:text-base font-semibold ${themeClasses.text} truncate`}
                >
                  {platform.platformName}
                </h3>
                <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
                  {platform.clientCount}{" "}
                  {t("systemHealth.clients").toLowerCase()} •{" "}
                  {platform.totalUnits.toLocaleString()}{" "}
                  {t("systemHealth.units").toLowerCase()} •{" "}
                  {platform.averageService}%
                </p>
              </div>
            </div>
            <button
              onClick={() => exportCsv(platform)}
              disabled={!platform.companies?.length}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <i className="ri-download-line"></i>
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-200 dark:divide-gray-700">
            {/* Healthy Clients */}
            <div className="p-3 sm:p-6">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <span
                  className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}
                >
                  {t("systemHealth.healthy")}
                </span>
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-[#22c55e] rounded-full"></div>
              </div>
              <p
                className={`text-xl sm:text-3xl font-bold ${themeClasses.text}`}
              >
                {platform.healthy}
              </p>
              <p
                className={`text-xs ${themeClasses.textSecondary} hidden sm:block`}
              >
                {t("systemHealth.service76_100")}
              </p>
            </div>

            {/* Warning */}
            <div className="p-3 sm:p-6">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <span
                  className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}
                >
                  {t("systemHealth.warning")}
                </span>
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-[#eab308] rounded-full"></div>
              </div>
              <p
                className={`text-xl sm:text-3xl font-bold ${themeClasses.text}`}
              >
                {platform.warning}
              </p>
              <p
                className={`text-xs ${themeClasses.textSecondary} hidden sm:block`}
              >
                {t("systemHealth.service51_75")}
              </p>
            </div>

            {/* High Risk */}
            <div className="p-3 sm:p-6">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <span
                  className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}
                >
                  {t("systemHealth.highRisk")}
                </span>
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-[#f59e0b] rounded-full"></div>
              </div>
              <p
                className={`text-xl sm:text-3xl font-bold ${themeClasses.text}`}
              >
                {platform.highRisk}
              </p>
              <p
                className={`text-xs ${themeClasses.textSecondary} hidden sm:block`}
              >
                {t("systemHealth.service26_50")}
              </p>
            </div>

            {/* Critical */}
            <div className="p-3 sm:p-6">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <span
                  className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}
                >
                  {t("systemHealth.critical")}
                </span>
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-[#ef4444] rounded-full"></div>
              </div>
              <p
                className={`text-xl sm:text-3xl font-bold ${themeClasses.text}`}
              >
                {platform.critical}
              </p>
              <p
                className={`text-xs ${themeClasses.textSecondary} hidden sm:block`}
              >
                {t("systemHealth.service0_25")}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StackedBarChart({ data, themeClasses, t }: StackedBarChartProps) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setAnimated(false);
    const timer = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(timer);
  }, [data]);

  // Usar el totalUnits más alto como 100% del eje Y
  const maxTotalUnits = Math.max(...data.map((d) => d.totalUnits || 0));

  // Redondear al múltiplo de 100 más cercano hacia arriba
  const roundedMax =
    maxTotalUnits > 0 ? Math.ceil(maxTotalUnits / 100) * 100 : 0;

  // Distribuir el eje Y en 8 niveles con números redondos (de abajo hacia arriba)
  const yAxisValues =
    roundedMax > 0
      ? [
          0,
          Math.round(roundedMax * 0.125),
          Math.round(roundedMax * 0.25),
          Math.round(roundedMax * 0.375),
          Math.round(roundedMax * 0.5),
          Math.round(roundedMax * 0.625),
          Math.round(roundedMax * 0.75),
          roundedMax,
        ]
      : [0, 5, 10, 15, 30, 50, 80, 100];

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}
    >
      {/* Legend and Filter */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-4 mb-2">
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#22c55e] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.healthy")}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#e1d82e] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.warning")}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#f59e0b] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.highRisk")}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#ef4444] rounded"></div>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.critical")}
            </span>
          </div>
        </div>
        {/* <button
          className={`flex items-center justify-center gap-2 px-3 py-1.5 text-xs rounded-lg border ${themeClasses.cardBorder} ${themeClasses.textSecondary} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors self-start sm:self-auto`}
        >
          <i className="ri-filter-3-line"></i>
          <span className="hidden sm:inline">Filter by Risk</span>
          <span className="sm:hidden">Filter</span>
        </button> */}
      </div>

      {/* Chart Container */}
      <div className="flex">
        {/* Y Axis */}
        <div className="flex flex-col justify-between h-72 sm:h-64 pr-2 sm:pr-3 text-right">
          {[...yAxisValues].reverse().map((val, idx) => (
            <span
              key={`${val}-${idx}`}
              className={`text-xs ${themeClasses.text}`}
            >
              {val}
            </span>
          ))}
        </div>

        {/* Bars */}
        <div className="flex-1 flex justify-around items-end h-48 sm:h-64 border-l border-b border-gray-200 dark:border-gray-700">
          {data.map((item) => {
            // Ajustar scale al contenedor real (192px en móvil, 256px en desktop)
            const containerHeight = window.innerWidth >= 640 ? 256 : 192;
            const scale = containerHeight / roundedMax;

            // Usar averagePercentage de healthSummary para calcular alturas
            const healthyHeight =
              (((item.healthy?.averagePercentage || 0) * item.totalUnits) /
                100) *
              scale;
            const warningHeight =
              (((item.warning?.averagePercentage || 0) * item.totalUnits) /
                100) *
              scale;
            const highRiskHeight =
              (((item.highRisk?.averagePercentage || 0) * item.totalUnits) /
                100) *
              scale;
            const criticalHeight =
              (((item.critical?.averagePercentage || 0) * item.totalUnits) /
                100) *
              scale;

            return (
              <div
                key={item.platform}
                className="flex flex-col items-center flex-1 max-w-24"
              >
                <div className="flex flex-col-reverse w-full">
                  {healthyHeight > 0 && (
                    <div
                      className="w-full bg-[#22c55e]"
                      style={{
                        height: animated ? `${healthyHeight}px` : "0px",
                        transition: "height 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    ></div>
                  )}
                  {warningHeight > 0 && (
                    <div
                      className="w-full bg-[#e1d82e]"
                      style={{
                        height: animated ? `${warningHeight}px` : "0px",
                        transition:
                          "height 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.1s",
                      }}
                    ></div>
                  )}
                  {highRiskHeight > 0 && (
                    <div
                      className="w-full bg-[#f59e0b]"
                      style={{
                        height: animated ? `${highRiskHeight}px` : "0px",
                        transition:
                          "height 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.2s",
                      }}
                    ></div>
                  )}
                  {criticalHeight > 0 && (
                    <div
                      className="w-full bg-[#ef4444] rounded-t-sm"
                      style={{
                        height: animated ? `${criticalHeight}px` : "0px",
                        transition:
                          "height 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.3s",
                      }}
                    ></div>
                  )}
                </div>
                <span
                  className={`text-xs ${themeClasses.text} translate-y-5 -mt-4 drop-shadow-sm`}
                >
                  {item.platform}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BarChartRow({
  label,
  percentage,
  color = "#22c55e",
  isDarkMode,
  themeClasses,
}: BarChartRowProps) {
  const [animatedWidth, setAnimatedWidth] = useState(0);

  useEffect(() => {
    setAnimatedWidth(0);
    const timer = setTimeout(() => setAnimatedWidth(percentage), 50);
    return () => clearTimeout(timer);
  }, [percentage]);

  return (
    <div className="flex items-center gap-4 py-2">
      <span className={`w-16 text-sm ${themeClasses.textSecondary} text-right`}>
        {label}
      </span>
      <div
        className={`flex-1 h-8 rounded ${isDarkMode ? "bg-[#2A2A2A]" : "bg-gray-100"}`}
      >
        <div
          className="h-full rounded"
          style={{
            width: `${animatedWidth}%`,
            backgroundColor: color,
            transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        ></div>
      </div>
      <span
        className={`w-14 text-sm font-medium ${themeClasses.text} text-right`}
      >
        {percentage}%
      </span>
    </div>
  );
}

function PlatformCard({
  icon,
  iconBgColor,
  name,
  percentage,
  current,
  total,
  isDarkMode,
  themeClasses,
  statusColor = "bg-brand-primary",
  t,
}: PlatformCardProps) {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  useEffect(() => {
    setAnimatedPercentage(0);
    const timer = setTimeout(() => setAnimatedPercentage(percentage), 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 ${iconBgColor} rounded-lg flex items-center justify-center`}
          >
            {icon}
          </div>
          <div>
            <p className={`text-sm font-semibold ${themeClasses.text}`}>
              {name}
            </p>
            {/* <p className={`text-xs ${themeClasses.textSecondary}`}>{label}</p> */}
          </div>
        </div>
        <div className={`w-2.5 h-2.5 ${statusColor} rounded-full`}></div>
      </div>

      <div className="flex items-baseline gap-1 mb-2">
        <span className={`text-3xl font-bold ${themeClasses.text}`}>
          {percentage}
        </span>
        <span className={`text-lg ${themeClasses.textSecondary}`}>%</span>
      </div>

      <div className="flex items-center justify-between">
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          {current.toLocaleString()} / {total.toLocaleString()}
        </p>
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          {t("systemHealth.units")}
        </p>
      </div>

      {/* Progress Bar */}
      <div
        className={`w-full h-1.5 rounded-full mt-2 ${isDarkMode ? "bg-[#2A2A2A]" : "bg-gray-100"}`}
      >
        <div
          className="h-full rounded-full transition-all duration-2000 ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{
            width: `${animatedPercentage}%`,
            background:
              statusColor === "bg-blue-400"
                ? "#60a5fa"
                : `linear-gradient(90deg, ${brand.primaryHover} 0%, ${brand.primary} 100%)`,
          }}
        ></div>
      </div>
    </div>
  );
}

// ─── Shared types ────────────────────────────────────────────────────────────
interface BillableCompany {
  id: string;
  name: string;
}
interface CompanyDevice {
  id: string;
  identifier: string | null;
  name: string;
  vehicleId: string | null;
}

const TREND_PLATFORMS = [
  { id: "v5", label: "V5", available: true },
  { id: "wialon", label: "Wialon", available: false },
  { id: "mapon", label: "Mapon", available: false },
] as const;

// ─── Shared UI helpers ────────────────────────────────────────────────────────
function PlatformPill({
  platform,
  onChange,
  themeClasses,
}: {
  platform: string;
  onChange: (p: string) => void;
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  return (
    <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
      {TREND_PLATFORMS.map((p) => (
        <button
          key={p.id}
          disabled={!p.available}
          onClick={() => onChange(p.id)}
          title={!p.available ? "Próximamente" : undefined}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            platform === p.id
              ? "bg-white dark:bg-gray-700 shadow-sm " + themeClasses.text
              : themeClasses.textSecondary +
                " hover:bg-gray-200 dark:hover:bg-gray-700"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function PeriodPill({
  period,
  onChange,
  themeClasses,
}: {
  period: "7d" | "30d" | "90d";
  onChange: (p: "7d" | "30d" | "90d") => void;
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  return (
    <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
      {(["7d", "30d", "90d"] as const).map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
            period === p
              ? "bg-white dark:bg-gray-700 shadow-sm " + themeClasses.text
              : themeClasses.textSecondary +
                " hover:bg-gray-200 dark:hover:bg-gray-700"
          }`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

// ─── CompanyTrendView ─────────────────────────────────────────────────────────
function CompanyTrendView({
  themeClasses,
}: {
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  const [platform, setPlatform] = useState("v5");
  const [companies, setCompanies] = useState<BillableCompany[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d");
  const [trendData, setTrendData] = useState<
    {
      date: string;
      reportingUnits: number;
      totalUnits: number;
      percentage: number;
    }[]
  >([]);
  const [totalUnits, setTotalUnits] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCompanies([]);
    setSelectedCompany("");
    setTrendData([]);
    if (platform !== "v5") return;
    setLoadingCompanies(true);
    api
      .get("/reportingBillableCompanies", { params: { platform } })
      .then((res) => setCompanies(res.data?.data?.companies ?? []))
      .catch(() => {})
      .finally(() => setLoadingCompanies(false));
  }, [platform]);

  useEffect(() => {
    if (!selectedCompany) return;
    setLoading(true);
    api
      .get("/reportingCompanyTrend", {
        params: { companyId: selectedCompany, period },
      })
      .then((res) => {
        const payload = res.data?.data;
        setTrendData(payload?.data ?? []);
        setTotalUnits(payload?.totalUnits ?? 0);
      })
      .catch(() => setTrendData([]))
      .finally(() => setLoading(false));
  }, [selectedCompany, period]);

  const avgPct =
    trendData.length > 0
      ? parseFloat(
          (
            trendData.reduce((s, d) => s + d.percentage, 0) / trendData.length
          ).toFixed(1),
        )
      : 0;
  const bestPct =
    trendData.length > 0
      ? parseFloat(Math.max(...trendData.map((d) => d.percentage)).toFixed(1))
      : 0;

  const last = Math.max(trendData.length - 1, 1);
  const chartPts = trendData.map((d, i) => ({
    x: (i / last) * 100,
    y: 100 - d.percentage,
  }));
  const polyLine = chartPts.map((p) => `${p.x},${p.y}`).join(" ");
  const areaLine =
    chartPts.length > 0
      ? `0,100 ${polyLine} ${chartPts[chartPts.length - 1].x},100`
      : "0,100 100,100";
  const midIdx = Math.floor(trendData.length / 2);

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-5`}
    >
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <PlatformPill
          platform={platform}
          onChange={setPlatform}
          themeClasses={themeClasses}
        />
        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          disabled={loadingCompanies || platform !== "v5"}
          className={`flex-1 min-w-[200px] px-3 py-1.5 text-xs rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text} focus:outline-none disabled:opacity-50 cursor-pointer`}
        >
          <option value="">
            {loadingCompanies ? "Cargando compañías…" : "Seleccionar compañía"}
          </option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <PeriodPill
          period={period}
          onChange={setPeriod}
          themeClasses={themeClasses}
        />
      </div>

      {!selectedCompany && (
        <div className="h-40 flex items-center justify-center">
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            Selecciona una compañía para ver las métricas de reporte
          </p>
        </div>
      )}

      {selectedCompany && (
        <>
          {loading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    Promedio del período
                  </p>
                  <p
                    className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}
                  >
                    {avgPct}%
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    Mejor día
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-[#22c55e]">
                    {bestPct}%
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    Total unidades
                  </p>
                  <p
                    className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}
                  >
                    {totalUnits}
                  </p>
                </div>
              </div>

              <div className="relative h-28">
                <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-right pr-2">
                  <span className={`text-[10px] ${themeClasses.textSecondary}`}>
                    100
                  </span>
                  <span className={`text-[10px] ${themeClasses.textSecondary}`}>
                    50
                  </span>
                  <span className={`text-[10px] ${themeClasses.textSecondary}`}>
                    0
                  </span>
                </div>
                <div className="ml-8 h-full">
                  <svg
                    className="w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <polygon
                      points={areaLine}
                      fill="#6366f1"
                      fillOpacity="0.12"
                    />
                    {polyLine && (
                      <polyline
                        points={polyLine}
                        fill="none"
                        stroke="#6366f1"
                        strokeWidth="2"
                        vectorEffect="non-scaling-stroke"
                      />
                    )}
                  </svg>
                </div>
                {trendData.length > 0 && (
                  <div className="ml-8 flex justify-between mt-1">
                    {[0, midIdx, trendData.length - 1].map((idx) => (
                      <span
                        key={idx}
                        className={`text-[10px] ${themeClasses.textSecondary}`}
                      >
                        {trendData[idx]?.date?.slice(5)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── DeviceTrendView ──────────────────────────────────────────────────────────
function DeviceTrendView({
  themeClasses,
}: {
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  const [platform, setPlatform] = useState("v5");
  const [companies, setCompanies] = useState<BillableCompany[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [devices, setDevices] = useState<CompanyDevice[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("7d");
  const [trendData, setTrendData] = useState<
    { date: string; reported: boolean; totalPoints: number }[]
  >([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCompanies([]);
    setSelectedCompany("");
    setDevices([]);
    setSelectedDevice("");
    setTrendData([]);
    if (platform !== "v5") return;
    setLoadingCompanies(true);
    api
      .get("/reportingBillableCompanies", { params: { platform } })
      .then((res) => setCompanies(res.data?.data?.companies ?? []))
      .catch(() => {})
      .finally(() => setLoadingCompanies(false));
  }, [platform]);

  useEffect(() => {
    setDevices([]);
    setSelectedDevice("");
    setTrendData([]);
    if (!selectedCompany) return;
    setLoadingDevices(true);
    api
      .get("/reportingCompanyDevices", {
        params: { companyId: selectedCompany },
      })
      .then((res) => setDevices(res.data?.data?.devices ?? []))
      .catch(() => {})
      .finally(() => setLoadingDevices(false));
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany || !selectedDevice) return;
    setLoading(true);
    api
      .get("/reportingDeviceTrend", {
        params: {
          companyId: selectedCompany,
          deviceId: selectedDevice,
          period,
        },
      })
      .then((res) => {
        const payload = res.data?.data;
        setTrendData(payload?.data ?? []);
        setTotalPoints(payload?.totalPoints ?? 0);
      })
      .catch(() => setTrendData([]))
      .finally(() => setLoading(false));
  }, [selectedCompany, selectedDevice, period]);

  const daysReported = trendData.filter((d) => d.reported).length;
  const maxPts = Math.max(...trendData.map((d) => d.totalPoints), 1);
  const barW = trendData.length > 0 ? 100 / trendData.length : 0;
  const midIdx = Math.floor(trendData.length / 2);
  const lastIdx = Math.max(trendData.length - 1, 0);

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-5`}
    >
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <PlatformPill
          platform={platform}
          onChange={setPlatform}
          themeClasses={themeClasses}
        />
        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          disabled={loadingCompanies || platform !== "v5"}
          className={`flex-1 min-w-[160px] px-3 py-1.5 text-xs rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text} focus:outline-none disabled:opacity-50 cursor-pointer`}
        >
          <option value="">
            {loadingCompanies ? "Cargando…" : "Seleccionar compañía"}
          </option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={selectedDevice}
          onChange={(e) => setSelectedDevice(e.target.value)}
          disabled={!selectedCompany || loadingDevices}
          className={`flex-1 min-w-[160px] px-3 py-1.5 text-xs rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text} focus:outline-none disabled:opacity-50 cursor-pointer`}
        >
          <option value="">
            {loadingDevices
              ? "Cargando…"
              : !selectedCompany
                ? "Primero selecciona compañía"
                : "Seleccionar unidad"}
          </option>
          {devices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <PeriodPill
          period={period}
          onChange={setPeriod}
          themeClasses={themeClasses}
        />
      </div>

      {(!selectedCompany || !selectedDevice) && (
        <div className="h-40 flex items-center justify-center">
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {!selectedCompany
              ? "Selecciona una compañía y una unidad"
              : "Selecciona una unidad para ver su historial de reportes"}
          </p>
        </div>
      )}

      {selectedCompany && selectedDevice && (
        <>
          {loading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    Total puntos GPS
                  </p>
                  <p
                    className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}
                  >
                    {totalPoints.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    Días reportando
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-[#22c55e]">
                    {daysReported}
                    <span
                      className={`text-xs font-normal ${themeClasses.textSecondary}`}
                    >
                      /{trendData.length}
                    </span>
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    Período
                  </p>
                  <p
                    className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}
                  >
                    {period}
                  </p>
                </div>
              </div>

              {trendData.length > 0 && (
                <div className="h-28">
                  <svg
                    className="w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    {trendData.map((d, i) => {
                      const bh = maxPts > 0 ? (d.totalPoints / maxPts) * 90 : 0;
                      return (
                        <rect
                          key={i}
                          x={i * barW + barW * 0.1}
                          y={100 - bh}
                          width={barW * 0.8}
                          height={bh}
                          fill={d.reported ? "#6366f1" : "#9ca3af"}
                          fillOpacity={d.reported ? 0.85 : 0.25}
                          rx="0.5"
                        />
                      );
                    })}
                  </svg>
                  <div className="flex justify-between mt-1">
                    {[0, midIdx, lastIdx].map((idx) => (
                      <span
                        key={idx}
                        className={`text-[10px] ${themeClasses.textSecondary}`}
                      >
                        {trendData[idx]?.date?.slice(5)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── TrendSection ─────────────────────────────────────────────────────────────
const TREND_VIEWS = [
  { id: "platform", label: "Plataforma" },
  { id: "company", label: "Compañía" },
  { id: "unit", label: "Unidad" },
] as const;

type TrendViewId = (typeof TREND_VIEWS)[number]["id"];

function TrendSection({
  themeClasses,
}: {
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  const [activeView, setActiveView] = useState<TrendViewId>("platform");

  return (
    <div>
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 mb-4 w-fit">
        {TREND_VIEWS.map((v) => (
          <button
            key={v.id}
            onClick={() => setActiveView(v.id)}
            className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${
              activeView === v.id
                ? "bg-white dark:bg-gray-700 shadow-sm " + themeClasses.text
                : themeClasses.textSecondary +
                  " hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {activeView === "platform" && (
        <HistoricalPerformanceTrends themeClasses={themeClasses} />
      )}
      {activeView === "company" && (
        <CompanyTrendView themeClasses={themeClasses} />
      )}
      {activeView === "unit" && <DeviceTrendView themeClasses={themeClasses} />}
    </div>
  );
}

function ReportingUnitsModal({
  platform,
  reporting,
  themeClasses,
  onClose,
}: {
  platform: ReportingPlatform;
  reporting: "yes" | "no";
  themeClasses: ReturnType<typeof getThemeClasses>;
  onClose: () => void;
}) {
  const { isDarkMode } = useDarkMode();
  const [search, setSearch] = useState("");
  const unitsQuery = useReportingUnits({ platform, reporting });
  const units = unitsQuery.data?.units ?? [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? units.filter(
        (u) =>
          (u.imei || "").toLowerCase().includes(q) ||
          (u.user_name || "").toLowerCase().includes(q) ||
          (u.business_name || "").toLowerCase().includes(q),
      )
    : units;
  const platformLabel = platformLabelOf(platform);
  const stateLabel = reporting === "yes" ? "Reportando" : "No reportando";

  const exportCsv = () => {
    const headers = ["IMEI", "Usuario", "Razón social"];
    const rows = filtered.map((u) => [u.imei, u.user_name || "", u.business_name || ""]);
    downloadCsv(
      `${platform}_${reporting === "yes" ? "reportando" : "no-reportando"}_${fileDate()}.csv`,
      toCsv(headers, rows),
    );
  };

  return (
    <Modal
      title={`${platformLabel} · ${stateLabel} (${units.length})`}
      themeClasses={themeClasses}
      onClose={onClose}
      width="w-full max-w-2xl"
      content={
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por IMEI, usuario o razón social…"
              className={`w-full sm:max-w-sm px-3 py-2 text-sm rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text} focus:outline-none`}
            />
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className={`text-xs ${themeClasses.textSecondary}`}>
                {filtered.length} de {units.length}
              </span>
              <button
                onClick={exportCsv}
                disabled={filtered.length === 0}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50`}
              >
                <i className="ri-download-line"></i>
                Export CSV
              </button>
            </div>
          </div>

          {unitsQuery.isLoading ? (
            <p className={`text-sm text-center py-8 ${themeClasses.textSecondary}`}>
              Cargando…
            </p>
          ) : filtered.length === 0 ? (
            <p className={`text-sm text-center py-8 ${themeClasses.textSecondary}`}>
              Sin resultados.
            </p>
          ) : (
            <div className={`rounded-lg border ${themeClasses.cardBorder} overflow-hidden`}>
              <div
                className={`grid grid-cols-3 gap-2 px-4 py-3 text-xs font-medium ${themeClasses.textSecondary} ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border-b ${themeClasses.cardBorder}`}
              >
                <span>IMEI</span>
                <span>Usuario</span>
                <span>Razón social</span>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[50vh] overflow-y-auto">
                {filtered.map((u, i) => (
                  <div
                    key={`${u.imei}-${i}`}
                    className={`grid grid-cols-3 gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
                  >
                    <span className={`${themeClasses.textSecondary} font-mono text-xs truncate`}>
                      {u.imei}
                    </span>
                    <span className={`${themeClasses.text} truncate`}>{u.user_name || "—"}</span>
                    <span className={`${themeClasses.text} truncate`}>{u.business_name || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      }
    />
  );
}

export default function SystemHealthOverview() {
  const { t } = useTranslation("entregaServicio");
  const { isDarkMode } = useDarkMode();
  const themeClasses = useMemo(() => getThemeClasses(isDarkMode), [isDarkMode]);

  const [reportingData, setReportingData] = useState({
    reportingCount: 0,
    previousCount: 0,
    changePercentage: 0,
    totalVehicles: 0,
    percentage: 0,
    billableCompaniesCount: 0,
    loading: true,
  });

  const summaryQuery = useReportingSummary();
  const summary = summaryQuery.data;
  const v5CompaniesQuery = useReportingCompanies({ platform: "v5" });
  const wialonCompaniesQuery = useReportingCompanies({ platform: "wialon" });
  const maponCompaniesQuery = useReportingCompanies({ platform: "mapon" });

  const v5Dist = computeRiskDistribution(v5CompaniesQuery.data ?? []);
  const wialonDist = computeRiskDistribution(wialonCompaniesQuery.data ?? []);
  const maponDist = computeRiskDistribution(maponCompaniesQuery.data ?? []);

  const buildBar = (
    platform: string,
    dist: ReturnType<typeof computeRiskDistribution>,
    total?: number,
  ): StackedBarData => {
    const totalCompanies =
      dist.buckets.healthy.companyCount +
        dist.buckets.warning.companyCount +
        dist.buckets.highRisk.companyCount +
        dist.buckets.critical.companyCount || 1;
    const cat = (b: "healthy" | "warning" | "highRisk" | "critical") => ({
      averagePercentage:
        Math.round((dist.buckets[b].companyCount / totalCompanies) * 1000) / 10,
      companyCount: dist.buckets[b].companyCount,
      units: dist.buckets[b].units,
    });
    return {
      platform,
      totalUnits: total ?? dist.totalUnits,
      healthy: cat("healthy"),
      warning: cat("warning"),
      highRisk: cat("highRisk"),
      critical: cat("critical"),
    };
  };

  const realStackedBarData: StackedBarData[] = [
    buildBar("V5", v5Dist, summary?.by_platform?.v5?.total),
    buildBar("Wialon", wialonDist, summary?.by_platform?.wialon?.total),
    buildBar("Mapon", maponDist, summary?.by_platform?.mapon?.total),
  ];

  const dumaxPct = summary?.effectiveness_pct ?? reportingData.percentage;
  const dumaxCompanies =
    summary?.companies_total ?? reportingData.billableCompaniesCount;
  const dumaxReporting = summary?.reporting ?? reportingData.reportingCount;
  const dumaxTotal = summary?.total ?? reportingData.totalVehicles;
  const dumaxNotReporting =
    summary?.not_reporting ?? Math.max(0, dumaxTotal - dumaxReporting);
  const wialonStats = summary?.by_platform?.wialon;
  const maponStats = summary?.by_platform?.mapon;
  const v5Stats = summary?.by_platform?.v5;
  const unattributed = summary?.by_platform?.unattributed;

  const [showUnattributed, setShowUnattributed] = useState(false);
  const [unattrSearch, setUnattrSearch] = useState("");
  const [unitsModal, setUnitsModal] = useState<{
    platform: ReportingPlatform;
    reporting: "yes" | "no";
  } | null>(null);
  const unattributedUnitsQuery = useReportingUnits({
    platform: "unattributed",
    reporting: "no",
  });
  const unattributedUnits = unattributedUnitsQuery.data?.units ?? [];
  const unattrFiltered = unattributedUnits.filter((u) => {
    const q = unattrSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (u.imei || "").toLowerCase().includes(q) ||
      (u.user_name || "").toLowerCase().includes(q) ||
      (u.business_name || "").toLowerCase().includes(q)
    );
  });
  const exportUnattributed = () => {
    const headers = ["IMEI", "Usuario", "Razón social"];
    const rows = unattrFiltered.map((u) => [
      u.imei,
      u.user_name || "",
      u.business_name || "",
    ]);
    downloadCsv(
      `unidades_sin_plataforma_${fileDate()}.csv`,
      toCsv(headers, rows),
    );
  };

  const bestWorst = (companies: typeof v5CompaniesQuery.data) => {
    const withUnits = (companies ?? []).filter(
      (c) => c.total >= REPORTING_DEFAULTS.minUnits,
    );
    if (!withUnits.length) return { best: 0, worst: 0 };
    const pcts = withUnits.map((c) => c.effectiveness_pct);
    return { best: Math.max(...pcts), worst: Math.min(...pcts) };
  };
  const v5BW = bestWorst(v5CompaniesQuery.data);
  const wialonBW = bestWorst(wialonCompaniesQuery.data);
  const maponBW = bestWorst(maponCompaniesQuery.data);

  const clientCountOf = (d: ReturnType<typeof computeRiskDistribution>) =>
    d.buckets.healthy.companyCount +
    d.buckets.warning.companyCount +
    d.buckets.highRisk.companyCount +
    d.buckets.critical.companyCount;


  useEffect(() => {
    const fetchReportingDevices = async () => {
      try {
        const res = await api.get("/deviceReporting24h");
        const platforms: Array<{
          platform: string;
          totalReportingUnits: number;
          previousCount: number;
          changePercentage: number;
          totalUnits: number;
          totalOfflineUnits: number;
          percentage: number;
          totalBillableCompanies: number;
          healthSummary: {
            healthy: {
              averagePercentage: number;
              companyCount: number;
              units: number;
              unitsList?: UnitItem[];
              companies?: CriticalCompany[];
            };
            warning: {
              averagePercentage: number;
              companyCount: number;
              units: number;
              unitsList?: UnitItem[];
              companies?: CriticalCompany[];
            };
            highRisk: {
              averagePercentage: number;
              companyCount: number;
              units: number;
              unitsList?: UnitItem[];
              companies?: CriticalCompany[];
            };
            critical: {
              averagePercentage: number;
              companyCount: number;
              units: number;
              unitsList?: UnitItem[];
              companies?: CriticalCompany[];
            };
          };
          ranking: {
            best: {
              id: string;
              percentage: number;
              company: {
                id: string;
                name: string;
                email: string;
                phone: string;
                status: string;
              } | null;
            };
            worst: {
              id: string;
              percentage: number;
              company: {
                id: string;
                name: string;
                email: string;
                phone: string;
                status: string;
              } | null;
            };
          };
        }> = res.data?.data ?? [];
        const v5 = platforms.find((p) => p.platform === "v5");
        setReportingData({
          reportingCount: v5?.totalReportingUnits ?? 0,
          previousCount: v5?.previousCount ?? 0,
          changePercentage: v5?.changePercentage ?? 0,
          totalVehicles: v5?.totalUnits ?? 0,
          percentage: v5?.percentage ?? 0,
          billableCompaniesCount: v5?.totalBillableCompanies ?? 0,
          loading: false,
        });

      } catch {
        setReportingData((prev) => ({ ...prev, loading: false }));
      }
    };
    fetchReportingDevices();
  }, []);

  return (
    <div className="w-full h-full overflow-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div>
          <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
            Resumen
          </h1>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("systemHealth.subtitle")}
          </p>
        </div>
      </div>

      <div className="mb-4 sm:mb-6">
        <AltasBajasPlatformCards themeClasses={themeClasses} />
      </div>

      {/* Main Grid */}
      {reportingData.loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <Skeleton className="col-span-2 lg:col-span-1 h-48 lg:row-span-2" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : null}

      {/* Section 1: System Health Overview */}
      <div className={reportingData.loading ? "hidden" : ""}>
        {/* Fila 1: card grande + 2 cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Overall Service Availability - Large Card */}
        <div
          className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className={`text-sm font-medium ${themeClasses.text}`}>
                {t("systemHealth.overallServiceAvailability")}
              </h3>
              <p className={`text-xs ${themeClasses.textSecondary}`}>
                {t("systemHealth.last24Hours")}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-white text-xl"></i>
            </div>
          </div>

          <div className="mb-4">
            <span
              className={`text-3xl sm:text-5xl font-bold ${themeClasses.text}`}
            >
              {reportingData.loading ? "..." : dumaxPct}
            </span>
            <span
              className={`text-xl sm:text-3xl font-bold ${themeClasses.textSecondary}`}
            >
              %
            </span>
          </div>

          {/* "% vs ayer" — comentado (requiere histórico) */}
          {false && (
          <div className="flex items-center gap-2 mb-4">
            <span
              className={`${reportingData.changePercentage >= 0 ? "" : "text-red-500"} text-sm font-medium flex items-center`}
              style={
                reportingData.changePercentage >= 0
                  ? { color: brandTextColor }
                  : undefined
              }
            >
              <i
                className={`${reportingData.changePercentage >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} mr-1`}
              ></i>
              {Math.abs(reportingData.changePercentage)}%
            </span>
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {t("systemHealth.vsYesterday")}
            </span>
          </div>
          )}

          {/* Progress Bar */}
          <div
            className={`w-full h-8 rounded-lg overflow-hidden ${isDarkMode ? "bg-[#2A2A2A]" : "bg-gray-100"}`}
          >
            <div
              className="h-full rounded-lg"
              style={{
                width: `${dumaxPct}%`,
                background: `linear-gradient(90deg, ${brand.primaryHover} 0%, ${brand.primary} 100%)`,
              }}
            ></div>
          </div>
        </div>

        {/* Columna derecha: 2 cards apiladas */}
        <div className="grid grid-rows-2 gap-3 sm:gap-4">
        {/* Total Platforms Card */}
        <StatCard
          icon={<i className="ri-stack-line text-white text-sm"></i>}
          label={t("systemHealth.platformsLabel")}
          value={3}
          sublabel={t("systemHealth.total")}
          iconBgColor="bg-blue-500"
        />

        {/* Total Companies Card */}
        <StatCard
          icon={<i className="ri-building-line text-white text-sm"></i>}
          label={t("systemHealth.companies")}
          value={reportingData.loading ? "..." : dumaxCompanies}
          sublabel={t("systemHealth.total")}
          iconBgColor="bg-indigo-500"
        />
        </div>
        </div>

        {/* Fila 2: 3 cards de unidades */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
        {/* total units reporting 24h Card */}
        <StatCard
          icon={
            <div className="relative">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <i className="ri-truck-line text-white text-sm"></i>
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#22c55e] rounded-full animate-pulse flex items-center justify-center">
                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
              </div>
            </div>
          }
          label="Reportando"
          value={reportingData.loading ? "..." : dumaxReporting}
          sublabel="Últimas 24h"
          iconBgColor="bg-transparent"
        />

        {/* No reportando Card */}
        <StatCard
          icon={<i className="ri-signal-wifi-off-line text-white text-sm"></i>}
          label="No reportando"
          value={reportingData.loading ? "..." : dumaxNotReporting}
          sublabel="Últimas 24h"
          iconBgColor="bg-red-500"
        />

        {/* Total units Card */}
        <StatCard
          icon={<i className="ri-truck-line text-white text-sm"></i>}
          label="Total unidades"
          value={reportingData.loading ? "..." : dumaxTotal}
          sublabel="Facturables"
          iconBgColor="bg-orange-500"
        />
        </div>
      </div>

      {/* Unattributed: unidades facturables sin plataforma (alerta crítica extra) */}
      {!reportingData.loading && unattributed && unattributed.total > 0 && (
        <button
          onClick={() => setShowUnattributed(true)}
          className="w-full text-left mt-4 sm:mt-6 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 sm:p-5 flex items-start gap-3 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
          </div>
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-semibold text-red-700 dark:text-red-400">
              {unattributed.total} unidades facturables sin plataforma
            </p>
            <p className="text-xs text-red-600 dark:text-red-400/80">
              No encontradas en Wialon, Mapon ni V5 · {unattributed.companies}{" "}
              compañías · 0% reportando
            </p>
          </div>
          <span className="ml-auto self-center flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400 whitespace-nowrap">
            Ver unidades <i className="ri-arrow-right-line"></i>
          </span>
        </button>
      )}

      {showUnattributed && (
        <Modal
          title={`Unidades facturables sin plataforma (${unattributed?.total ?? unattributedUnits.length})`}
          themeClasses={themeClasses}
          onClose={() => setShowUnattributed(false)}
          width="w-full max-w-2xl"
          content={
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                <input
                  type="text"
                  value={unattrSearch}
                  onChange={(e) => setUnattrSearch(e.target.value)}
                  placeholder="Buscar por IMEI, usuario o razón social…"
                  className={`w-full sm:max-w-sm px-3 py-2 text-sm rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text} focus:outline-none`}
                />
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs ${themeClasses.textSecondary}`}>
                    {unattrFiltered.length} de {unattributedUnits.length}
                  </span>
                  <button
                    onClick={exportUnattributed}
                    disabled={unattrFiltered.length === 0}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50`}
                  >
                    <i className="ri-download-line"></i>
                    Export CSV
                  </button>
                </div>
              </div>

              {unattributedUnitsQuery.isLoading ? (
                <p className={`text-sm text-center py-8 ${themeClasses.textSecondary}`}>
                  Cargando…
                </p>
              ) : unattrFiltered.length === 0 ? (
                <p className={`text-sm text-center py-8 ${themeClasses.textSecondary}`}>
                  Sin resultados.
                </p>
              ) : (
                <div className={`rounded-lg border ${themeClasses.cardBorder} overflow-hidden`}>
                  <div className={`grid grid-cols-3 gap-2 px-4 py-3 text-xs font-medium ${themeClasses.textSecondary} ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border-b ${themeClasses.cardBorder}`}>
                    <span>IMEI</span>
                    <span>Usuario</span>
                    <span>Razón social</span>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[50vh] overflow-y-auto">
                    {unattrFiltered.map((u, i) => (
                      <div
                        key={`${u.imei}-${i}`}
                        className={`grid grid-cols-3 gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
                      >
                        <span className={`${themeClasses.textSecondary} font-mono text-xs truncate`}>{u.imei}</span>
                        <span className={`${themeClasses.text} truncate`}>{u.user_name || "—"}</span>
                        <span className={`${themeClasses.text} truncate`}>{u.business_name || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
        />
      )}

      {unitsModal && (
        <ReportingUnitsModal
          platform={unitsModal.platform}
          reporting={unitsModal.reporting}
          themeClasses={themeClasses}
          onClose={() => setUnitsModal(null)}
        />
      )}

      {/* Section 2: Platform Performance */}
      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
            <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
              {t("systemHealth.platformPerformance")}
            </h2>
            <span
              title={t("systemHealth.liveBadgeHint")}
              className={`text-[11px] whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
            >
              <i className="ri-broadcast-line" />
              {t("systemHealth.liveBadge")}
            </span>
          </div>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("systemHealth.platformPerformanceSubtitle")}
          </p>
        </div>

        {reportingData.loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4">
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* V2 Legacy */}
            {/* <PlatformCard
            icon={<i className="ri-cloud-line text-white text-sm"></i>}
            iconBgColor="bg-gray-500"
            name="V2"
            label="Legacy"
            percentage={97.2}
            current={1842}
            total={1895}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
          /> */}

            {/* V4 Standard */}
            {/* <PlatformCard
            icon={<i className="ri-server-line text-white text-sm"></i>}
            iconBgColor="bg-amber-500"
            name="V4"
            label="Standard"
            percentage={96.8}
            current={2134}
            total={2204}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
          /> */}

            {/* V5 Premium */}
            <PlatformCard
              icon={<i className="ri-rocket-line text-white text-sm"></i>}
              iconBgColor="bg-primary-500"
              name="V5"
              label="Premium"
              percentage={v5Stats?.effectiveness_pct ?? reportingData.percentage}
              current={v5Stats?.reporting ?? reportingData.reportingCount}
              total={v5Stats?.total ?? reportingData.totalVehicles}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              t={t}
            />

            {/* Wialon Partner */}
            <PlatformCard
              icon={<div className="w-3 h-3 bg-blue-400 rounded-full"></div>}
              iconBgColor="bg-transparent"
              name="Wialon"
              label="Partner"
              percentage={wialonStats?.effectiveness_pct ?? 0}
              current={wialonStats?.reporting ?? 0}
              total={wialonStats?.total ?? 0}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              statusColor="bg-blue-400"
              t={t}
            />

            {/* Mapon Partner */}
            <PlatformCard
              icon={<div className="w-3 h-3 bg-primary-500 rounded-full"></div>}
              iconBgColor="bg-transparent"
              name="Mapon"
              label="Partner"
              percentage={maponStats?.effectiveness_pct ?? 0}
              current={maponStats?.reporting ?? 0}
              total={maponStats?.total ?? 0}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              t={t}
            />
          </div>
        )}
      </div>

      {/* Dispositivos por plataforma: reportan / no reportan */}
      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            Dispositivos por plataforma
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Reportan vs no reportan por plataforma
          </p>
        </div>
        <div
          className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 overflow-x-auto`}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${themeClasses.cardBorder}`}>
                <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Plataforma</th>
                <th className={`text-center py-2 px-3 ${themeClasses.textSecondary}`}>Total</th>
                <th className={`text-center py-2 px-3 ${themeClasses.textSecondary}`}>Reportan</th>
                <th className={`text-center py-2 px-3 ${themeClasses.textSecondary}`}>No reportan</th>
                <th className={`text-center py-2 px-3 ${themeClasses.textSecondary}`}>Efectividad</th>
              </tr>
            </thead>
            <tbody>
              {(["wialon", "mapon", "v5", "unattributed"] as const).map((p) => {
                const s = summary?.by_platform?.[p];
                return (
                  <tr key={p} className={`border-b ${themeClasses.cardBorder}`}>
                    <td className={`py-2 px-3 ${themeClasses.text}`}>{platformLabelOf(p)}</td>
                    <td className={`py-2 px-3 text-center ${themeClasses.text}`}>{s?.total ?? 0}</td>
                    <td className="py-2 px-3 text-center">
                      <button
                        type="button"
                        disabled={!s?.reporting}
                        onClick={() => setUnitsModal({ platform: p, reporting: "yes" })}
                        className="text-green-500 font-medium hover:underline disabled:no-underline disabled:opacity-60 disabled:cursor-default"
                      >
                        {s?.reporting ?? 0}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button
                        type="button"
                        disabled={!s?.not_reporting}
                        onClick={() => setUnitsModal({ platform: p, reporting: "no" })}
                        className="text-red-500 font-medium hover:underline disabled:no-underline disabled:opacity-60 disabled:cursor-default"
                      >
                        {s?.not_reporting ?? 0}
                      </button>
                    </td>
                    <td className={`py-2 px-3 text-center ${themeClasses.text}`}>{s?.effectiveness_pct ?? 0}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3: Platform Service Comparison Chart */}
      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            {t("systemHealth.platformComparison")}
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("systemHealth.platformComparisonSubtitle")}
          </p>
        </div>

        {reportingData.loading ? (
          <div
            className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 space-y-3`}
          >
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
          </div>
        ) : (
          <div
            className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}
          >
            <div className="space-y-1">
              <BarChartRow
                label="Mapon"
                percentage={maponStats?.effectiveness_pct ?? 0}
                color={getEffectivenessBarColor(maponStats?.effectiveness_pct ?? 0)}
                isDarkMode={isDarkMode}
                themeClasses={themeClasses}
              />
              <BarChartRow
                label="Wialon"
                percentage={wialonStats?.effectiveness_pct ?? 0}
                color={getEffectivenessBarColor(wialonStats?.effectiveness_pct ?? 0)}
                isDarkMode={isDarkMode}
                themeClasses={themeClasses}
              />
              <BarChartRow
                label="V5"
                percentage={v5Stats?.effectiveness_pct ?? reportingData.percentage}
                color={getEffectivenessBarColor(v5Stats?.effectiveness_pct ?? reportingData.percentage)}
                isDarkMode={isDarkMode}
                themeClasses={themeClasses}
              />
            </div>

            {/* X Axis */}
            <div className="flex justify-between mt-4 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className={`text-xs ${themeClasses.textSecondary}`}>0</span>
              <span className={`text-xs ${themeClasses.textSecondary}`}>
                20
              </span>
              <span className={`text-xs ${themeClasses.textSecondary}`}>
                40
              </span>
              <span className={`text-xs ${themeClasses.textSecondary}`}>
                60
              </span>
              <span className={`text-xs ${themeClasses.textSecondary}`}>
                80
              </span>
              <span className={`text-xs ${themeClasses.textSecondary}`}>
                100
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Section 4: Client Risk Distribution Chart */}
      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            {t("systemHealth.clientRiskDistribution")}
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("systemHealth.clientRiskDistributionSubtitle")}
          </p>
        </div>

        {reportingData.loading ? (
          <Skeleton className="h-64" />
        ) : (
          <StackedBarChart
            data={realStackedBarData}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            t={t}
          />
        )}
      </div>

      {/* Section 5: Detailed Platform Analytics */}
      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            {t("systemHealth.DetailedPlatformsAnalytics")}
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("systemHealth.DetailedPlatformsAnalyticsSubtitle")}
          </p>
        </div>

        {reportingData.loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Skeleton className="h-52" />
            <Skeleton className="h-52" />
            <Skeleton className="h-52" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* V5 Platform */}
            <PlatformAnalyticsCard
              icon={<i className="ri-cloud-line text-white text-lg"></i>}
              iconBgColor="bg-gray-500"
              name="V5"
              description="Legacy System"
              clientCount={v5Stats?.companies ?? 0}
              serviceRate={v5Stats?.effectiveness_pct ?? 0}
              bestClient={v5BW.best}
              worstClient={v5BW.worst}
              chartData={[null, null, null, null, null, null, null]}
              chartColor="#6366f1"
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              t={t}
            />

            {/* V4 Platform */}
            <PlatformAnalyticsCard
              icon={<i className="ri-server-line text-white text-lg"></i>}
              iconBgColor="bg-amber-500"
              name="Mapon"
              description="Standard System"
              clientCount={maponStats?.companies ?? 0}
              serviceRate={maponStats?.effectiveness_pct ?? 0}
              bestClient={maponBW.best}
              worstClient={maponBW.worst}
              chartData={[null, null, null, null, null, null, null]}
              chartColor="#6366f1"
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              t={t}
            />

            {/* V5 Platform */}
            {/* <PlatformAnalyticsCard
            icon={<i className="ri-rocket-line text-white text-lg"></i>}
            iconBgColor="bg-primary-500"
            name="V5 Platform"
            description="Premium System"
            clientCount={24}
            serviceRate={98.4}
            bestClient={100}
            worstClient={89.2}
            chartData={[98, 98.2, 98.5, 98.3, 98.6, 98.4, 98.4]}
            chartColor="#22c55e"
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
          /> */}

            {/* Wialon Platform */}
            <PlatformAnalyticsCard
              icon={<i className="ri-circle-fill text-blue-400 text-lg"></i>}
              iconBgColor="bg-blue-100"
              name="Wialon"
              description="Partner System"
              clientCount={wialonStats?.companies ?? 0}
              serviceRate={wialonStats?.effectiveness_pct ?? 0}
              bestClient={wialonBW.best}
              worstClient={wialonBW.worst}
              chartData={[null, null, null, null, null, null, null]}
              chartColor="#f59e0b"
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              t={t}
            />
          </div>
        )}
      </div>

      {/* Section 6: Critical Alerts & Action Required */}
      {/* <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            {t("systemHealth.criticalAlerts.alertsTable.title")}
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("systemHealth.criticalAlerts.alertsTable.subtitle")}
          </p>
        </div>

        {reportingData.loading ? (
          <div
            className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}
          >
            <div className="p-4 space-y-3">
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
            </div>
          </div>
        ) : (
          <CompanyAlertsTable
            companies={v5AllCompanies}
            allUnits={v5AllUnits}
            themeClasses={themeClasses}
            t={t}
          />
        )}
      </div> */}

      {/*Section 7:  Platform Client Details */}
      <div className="mt-6 sm:mt-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            {t("systemHealth.platformClientDetails.title")}
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {t("systemHealth.platformClientDetails.subtitle")}
          </p>
        </div>

        {reportingData.loading ? (
          <div className="space-y-3">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        ) : (
          <PlatformClientDetails
            platforms={[
              {
                platformName: "V5",
                platformIcon: (
                  <i className="ri-cloud-line text-white text-lg"></i>
                ),
                iconBgColor: "bg-gray-500",
                clientCount: clientCountOf(v5Dist),
                totalUnits: v5Stats?.total ?? v5Dist.totalUnits,
                averageService: v5Stats?.effectiveness_pct ?? 0,
                healthy: v5Dist.buckets.healthy.companyCount,
                warning: v5Dist.buckets.warning.companyCount,
                highRisk: v5Dist.buckets.highRisk.companyCount,
                critical: v5Dist.buckets.critical.companyCount,
                companies: v5CompaniesQuery.data ?? [],
              },
              {
                platformName: "Mapon",
                platformIcon: (
                  <i className="ri-server-line text-white text-lg"></i>
                ),
                iconBgColor: "bg-amber-500",
                clientCount: clientCountOf(maponDist),
                totalUnits: maponStats?.total ?? maponDist.totalUnits,
                averageService: maponStats?.effectiveness_pct ?? 0,
                healthy: maponDist.buckets.healthy.companyCount,
                warning: maponDist.buckets.warning.companyCount,
                highRisk: maponDist.buckets.highRisk.companyCount,
                critical: maponDist.buckets.critical.companyCount,
                companies: maponCompaniesQuery.data ?? [],
              },
              {
                platformName: "Wialon",
                platformIcon: (
                  <i className="ri-wifi-line text-white text-lg"></i>
                ),
                iconBgColor: "bg-orange-500",
                clientCount: clientCountOf(wialonDist),
                totalUnits: wialonStats?.total ?? wialonDist.totalUnits,
                averageService: wialonStats?.effectiveness_pct ?? 0,
                healthy: wialonDist.buckets.healthy.companyCount,
                warning: wialonDist.buckets.warning.companyCount,
                highRisk: wialonDist.buckets.highRisk.companyCount,
                critical: wialonDist.buckets.critical.companyCount,
                companies: wialonCompaniesQuery.data ?? [],
              },
            ]}
            themeClasses={themeClasses}
            t={t}
          />
        )}
      </div>

      {/* Section 9: Historial mensual — dos gráficas (Unidades Activas + Altas/Bajas) */}
      <div className="mt-6 sm:mt-8">
        <ActiveUnitsHistorySection themeClasses={themeClasses} />
      </div>
    </div>
  );
}
